// ============================================================
// SalesTab.jsx  —  Noble Enterprises (tape module)
//
// WHAT'S ON THIS SCREEN
//   • Invoice list, click a row to see its lines
//   • New invoice — pick a customer, pick sizes, set rates
//   • Customer balances, with payment and ledger buttons
//
// STOCK IS PURE SPEC
// A line names a size: type, micron, width, length. There is no
// product catalogue and nothing is reserved for a particular
// customer, so every size on the shelf can go to anyone. That is
// why the stock list loads once rather than per customer.
//
// A SIZE WITHOUT A PRICE CANNOT BE INVOICED
// The rate defaults from the price set on the Finished stock
// screen. Sizes with no price still appear, flagged, so the
// seller can see they exist and why they are blocked, rather
// than wondering where the stock went.
//
// HOW THIS FILE IS ORGANISED
//   A. Shared UI bits
//   B. LineItemsEditor
//   C. InvoiceForm
//   D. InvoiceDetailRow
//   E. PaymentModal + LedgerModal
//   F. SalesTab
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import WindowModal, { DEFAULT_FROM_DATE } from '../components/WindowModal';
import PrintButton from '../print/PrintButton';
import { invoicePrintConfig } from './reports/invoicePrint.config';
import { ledgerConfig } from './reports/ledger.config';
import {
  Plus, Trash2, X, Check, Wallet, FileText, Lock,
  ChevronDown as Chevron,
} from 'lucide-react';
import { hasFullAccess } from '../../../utils/access';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const C = {
  brand:      '#f39c12',
  text:       '#1a2332',
  textSoft:   '#64748b',
  textMuted:  '#94a3b8',
  border:     '#e2e8f0',
  borderSoft: '#f1f5f9',
  card:       '#ffffff',
  rowAlt:     '#fafbfc',
  danger:     '#dc2626',
  dangerBg:   '#fee2e2',
  success:    '#059669',
  successBg:  '#d1fae5',
  warning:    '#d97706',
  warningBg:  '#fef3c7',
  info:       '#0284c7',
  infoBg:     '#e0f2fe',
};

const num    = (n) => Number(n || 0);
const money  = (n) => `Rs ${num(n).toLocaleString('en-PK', { maximumFractionDigits: 0 })}`;
const today  = () => new Date().toISOString().split('T')[0];
const fmtDate = (d) => new Date(d).toLocaleDateString('en-GB');

// Postgres numerics arrive as strings with trailing zeros
const mm = (n) => `${num(n).toLocaleString('en-PK', { maximumFractionDigits: 2 })}mm`;
const yd = (n) => `${num(n).toLocaleString('en-PK', { maximumFractionDigits: 2 })}yd`;

// One label for a size, used everywhere a product name used to be
const specOf = (s) =>
  `${s.tape_type_name} ${num(s.micron_value)}µ · ${mm(s.width_mm)} × ${yd(s.length_yards)}`;

// Must match PAYMENT_DELETE_WINDOW_DAYS in sales.model.js. This only
// hides the button; the server decides.
const PAYMENT_DELETE_DAYS = 30;

const currentUser = () => {
  try {
    return JSON.parse(atob(localStorage.getItem('token').split('.')[1]));
  } catch {
    return {};
  }
};

const canDeletePayments = () => hasFullAccess('adhesive');

// Same owner check; named for what it gates here.
// The invoice form shows cost to every tape user — the seller
// prices against it. Deletes stay owner / module-admin only.
const canSeeCost = () => true;

// Measured from when it was typed in, not the date written on it.
const withinDeleteWindow = (createdAt) => {
  if (!createdAt) return false;
  return (Date.now() - new Date(createdAt).getTime()) / 86400000 <= PAYMENT_DELETE_DAYS;
};

const deletePaymentRequest = (id) =>
  axios.delete(`${API}/tape/sales/payments/${id}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  });

const confirmDeletePayment = (amount, date) =>
  window.confirm(
    `Delete the payment of ${money(amount)} dated ${fmtDate(date)}?\n\n`
    + `The customer's balance goes back up by that amount. `
    + `This cannot be undone — re-enter it correctly afterwards.`
  );


// ============================================================
// A. SHARED UI BITS
// ============================================================

const inputStyle = {
  width: '100%', padding: '10px 13px',
  border: `1px solid ${C.border}`, borderRadius: '8px',
  fontSize: '14px', color: C.text, boxSizing: 'border-box',
  fontFamily: 'inherit', background: '#ffffff',
};

const Field = ({ label, children, hint }) => (
  <div>
    <label style={{
      display: 'block', fontSize: '12px', fontWeight: '600',
      color: C.textSoft, marginBottom: '6px',
    }}>
      {label}
    </label>
    {children}
    {hint && <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '5px' }}>{hint}</div>}
  </div>
);

const TextInput = (props) => <input style={inputStyle} {...props} />;

const Btn = ({ variant = 'primary', children, ...props }) => {
  const styles = {
    primary: { background: C.brand,   color: '#ffffff',  border: 'none' },
    ghost:   { background: '#ffffff', color: C.textSoft, border: `1px solid ${C.border}` },
  };
  return (
    <button
      style={{
        ...styles[variant],
        padding: '10px 18px', borderRadius: '8px', fontSize: '14px',
        fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
        display: 'inline-flex', alignItems: 'center', gap: '7px',
      }}
      {...props}
    >
      {children}
    </button>
  );
};

const IconBtn = ({ danger, title, children, ...props }) => (
  <button
    title={title}
    style={{
      width: '32px', height: '32px', borderRadius: '7px',
      border: `1px solid ${C.border}`, background: '#ffffff',
      color: danger ? C.danger : C.textSoft, cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}
    {...props}
  >
    {children}
  </button>
);

const Banner = ({ type, message, onClose }) => {
  if (!message) return null;
  const isError = type === 'error';
  return (
    <div style={{
      background: isError ? C.dangerBg : C.successBg,
      color:      isError ? C.danger   : C.success,
      padding: '11px 16px', borderRadius: '8px', marginBottom: '16px',
      fontSize: '14px', display: 'flex', alignItems: 'center', gap: '10px',
    }}>
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', display: 'flex' }}>
        <X size={16} />
      </button>
    </div>
  );
};

const thStyle = {
  textAlign: 'left', padding: '11px 18px', fontSize: '12px',
  fontWeight: '600', color: C.textSoft, background: C.rowAlt,
  textTransform: 'uppercase', letterSpacing: '0.3px',
};
const tdStyle = {
  padding: '13px 18px', fontSize: '14px', color: C.text,
  borderTop: `1px solid ${C.borderSoft}`,
};

const PAYMENT_STATUS = {
  paid:    { label: 'Paid',    bg: C.successBg, fg: C.success },
  partial: { label: 'Partial', bg: C.warningBg, fg: C.warning },
  unpaid:  { label: 'Unpaid',  bg: C.dangerBg,  fg: C.danger  },
};

const PaymentBadge = ({ status }) => {
  const s = PAYMENT_STATUS[status] || PAYMENT_STATUS.unpaid;
  return (
    <span style={{
      fontSize: '12px', fontWeight: '600', padding: '3px 11px',
      borderRadius: '11px', background: s.bg, color: s.fg,
      whiteSpace: 'nowrap',
    }}>
      {s.label}
    </span>
  );
};


// ============================================================
// B. LINE ITEMS EDITOR
//
// Each line points at one stock row — one size. The dropdown
// shows what is left of each, so availability is visible without
// leaving the form.
// ============================================================

const blankLine = { stock_id: '', quantity: '', rate: '' };

const LineItemsEditor = ({ items, setItems, stock }) => {
  const updateLine = (index, patch) =>
    setItems(items.map((l, i) => (i === index ? { ...l, ...patch } : l)));

  const addLine    = () => setItems([...items, { ...blankLine }]);
  const removeLine = (index) => setItems(items.filter((_, i) => i !== index));

  // Picking a size fills the rate from the price set on the
  // Finished stock screen. The seller can still change it for a
  // particular deal.
  const pickStock = (index, stockId) => {
    const row = stock.find(s => String(s.stock_id) === String(stockId));
    updateLine(index, {
      stock_id: stockId,
      rate: num(row?.price) > 0 ? String(num(row.price)) : items[index].rate,
    });
  };

  const grandTotal = items.reduce(
    (sum, l) => sum + (num(l.quantity) * num(l.rate)), 0
  );

  const cell = { padding: '8px 10px', verticalAlign: 'top' };
  const head = {
    padding: '9px 10px', fontSize: '11px', fontWeight: '600',
    color: C.textSoft, textTransform: 'uppercase', letterSpacing: '0.3px',
    background: C.rowAlt, textAlign: 'left',
  };

  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: '10px', overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '660px' }}>
          <thead>
            <tr>
                            <th style={{ ...head, width: '31%' }}>Size</th>
                            <th style={{ ...head, width: '15%' }}>Qty</th>
              {canSeeCost() && <th style={{ ...head, width: '13%', textAlign: 'right' }}>Cost</th>}
              <th style={{ ...head, width: '18%' }}>Rate</th>
              <th style={{ ...head, width: '18%', textAlign: 'right' }}>Amount</th>
              <th style={{ ...head, width: '5%' }}></th>
            </tr>
          </thead>
          <tbody>
            {items.map((line, index) => {
              const row    = stock.find(s => String(s.stock_id) === String(line.stock_id));
              const amount = num(line.quantity) * num(line.rate);
              const over   = row && num(line.quantity) > num(row.quantity);

              // Selling under cost is legal and occasionally
              // deliberate — clearing old stock — so it warns
              // rather than blocks.
              const cost       = row ? num(row.cost_per_piece) : 0;
              const underCost  = row && num(line.rate) > 0 && num(line.rate) < cost;

              return (
                <tr key={index} style={{ borderTop: `1px solid ${C.borderSoft}` }}>
                  <td style={cell}>
                    <select
                      style={inputStyle}
                      value={line.stock_id}
                      onChange={e => pickStock(index, e.target.value)}
                    >
                      <option value="">Select size…</option>
                      {stock.map(s => (
                        <option key={s.stock_id} value={s.stock_id}>
                          {specOf(s)} · {s.quantity} available
                          {num(s.price) > 0 ? '' : ' · no price set'}
                        </option>
                      ))}
                    </select>
                    {row && (
                      <div style={{ marginTop: '6px', fontSize: '11px', color: C.textMuted }}>
                                                                        {row.quantity} in stock
                      </div>
                    )}
                  </td>
                  <td style={cell}>
                    <TextInput
                      type="number" min="1" step="1"
                      value={line.quantity}
                      onChange={e => updateLine(index, { quantity: e.target.value })}
                      placeholder="0"
                    />
                    {over && (
                      <div style={{ fontSize: '11px', color: C.danger, marginTop: '5px' }}>
                        Only {row.quantity} left
                      </div>
                    )}
                  </td>
                                    {canSeeCost() && (
                                        <td style={{ ...cell, textAlign: 'right', paddingTop: '18px', color: '#BD0F4C', fontWeight: '600' }}>
                      {row ? money(cost) : '—'}
                    </td>
                  )}
                  <td style={cell}>
                    <TextInput
                      type="number" step="0.01" min="0"
                      value={line.rate}
                      onChange={e => updateLine(index, { rate: e.target.value })}
                      placeholder="0"
                    />
                    {underCost && (
                      <div style={{ fontSize: '11px', color: C.danger, marginTop: '5px' }}>
                        Below the {money(cost)} it cost
                      </div>
                    )}
                  </td>
                  <td style={{ ...cell, textAlign: 'right', paddingTop: '18px', fontWeight: '600' }}>
                    {amount > 0 ? money(amount) : '—'}
                  </td>
                  <td style={{ ...cell, paddingTop: '14px' }}>
                    {items.length > 1 && (
                      <IconBtn danger title="Remove line" onClick={() => removeLine(index)}>
                        <Trash2 size={14} />
                      </IconBtn>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{
        padding: '12px 16px', borderTop: `1px solid ${C.borderSoft}`,
        display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap',
        background: C.rowAlt,
      }}>
        <button
          type="button"
          onClick={addLine}
          style={{
            padding: '8px 14px', borderRadius: '7px',
            border: `1px dashed ${C.border}`, background: '#ffffff',
            color: C.textSoft, fontSize: '13px', fontWeight: '600',
            cursor: 'pointer', fontFamily: 'inherit',
            display: 'inline-flex', alignItems: 'center', gap: '6px',
          }}
        >
          <Plus size={14} /> Add line
        </button>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'baseline', gap: '10px' }}>
          <span style={{ fontSize: '13px', color: C.textSoft, fontWeight: '600' }}>Invoice total</span>
          <span style={{ fontSize: '20px', fontWeight: '700', color: C.text }}>
            {money(grandTotal)}
          </span>
        </div>
      </div>
    </div>
  );
};


// ============================================================
// C. INVOICE FORM
//
// In a window, matching the cutting screen. The invoice list
// stays visible behind it, and a half-finished invoice can be
// minimised while the seller checks a balance.
// ============================================================

const emptyForm = () => ({
  customer_id: '',
  invoice_date: today(),
  notes: '',
  items: [{ ...blankLine }],
});

const InvoiceForm = ({
  form, setForm, customers, stock, loadingStock, onSave, onCancel, saving,
}) => {
  const noCustomer = !form.customer_id;
  const unpriced = stock.filter(s => !(num(s.price) > 0)).length;

  return (
    <WindowModal
      id="sales-invoice-form"
      title="New invoice"
      onClose={onCancel}
      width="880px"
    >
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px', marginBottom: '20px',
      }}>
        <Field label="Customer">
          <select
            style={inputStyle}
            value={form.customer_id}
            onChange={e => setForm({ ...form, customer_id: e.target.value })}
          >
            <option value="">Select customer…</option>
            {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>

        <Field label="Invoice date">
          <TextInput
            type="date"
            max={today()}
            value={form.invoice_date}
            onChange={e => setForm({ ...form, invoice_date: e.target.value })}
          />
        </Field>

        <Field label="Notes">
          <TextInput
            value={form.notes}
            onChange={e => setForm({ ...form, notes: e.target.value })}
            placeholder="Optional"
          />
        </Field>
      </div>

      {loadingStock ? (
        <p style={{ fontSize: '13px', color: C.textSoft }}>Loading stock…</p>
      ) : stock.length === 0 ? (
        <div style={{
          padding: '14px 18px', background: C.dangerBg, borderRadius: '10px',
          fontSize: '13px', color: C.danger, marginBottom: '20px',
        }}>
          Nothing in finished stock to sell. Record a cutting run first.
        </div>
      ) : (
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block', fontSize: '12px', fontWeight: '600',
            color: C.textSoft, marginBottom: '8px',
          }}>
            Lines
          </label>
          <LineItemsEditor
            items={form.items}
            setItems={items => setForm({ ...form, items })}
            stock={stock}
          />
          {unpriced > 0 && (
            <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '8px' }}>
              {unpriced} size{unpriced === 1 ? '' : 's'} have no price set — enter a rate by
              hand, or set one on the Finished stock screen.
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: '10px' }}>
        <Btn onClick={onSave} disabled={saving || noCustomer || stock.length === 0}>
          <Check size={16} /> {saving ? 'Saving…' : 'Create invoice'}
        </Btn>
        <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
      </div>
    </WindowModal>
  );
};


// ============================================================
// D. INVOICE DETAIL ROW
// ============================================================

const InvoiceDetailRow = ({ invoiceId, colSpan, notify, onChanged }) => {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  const mayDelete = canDeletePayments();

  const load = useCallback(() => {
    axios.get(`${API}/tape/sales/${invoiceId}`, { headers })
      .then(res => setInvoice(res.data.data))
      .catch(() => setInvoice(null))
      .finally(() => setLoading(false));
  }, [invoiceId]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  // Reloads this row and the list above it — the status badge and
  // the balance column both move when a payment goes.
  const removePayment = async (p) => {
    if (!confirmDeletePayment(p.amount, p.payment_date)) return;
    try {
      await deletePaymentRequest(p.id);
      notify('success', 'Payment deleted');
      load();
      onChanged();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not delete payment');
    }
  };

  const cell = { padding: '9px 12px', fontSize: '13px', borderTop: `1px solid ${C.borderSoft}` };
  const head = {
    ...cell, background: '#ffffff', fontWeight: '600', color: C.textSoft,
    fontSize: '11px', textTransform: 'uppercase', borderTop: 'none',
  };

  return (
    <tr>
      <td colSpan={colSpan} style={{
        padding: '18px 22px', background: C.rowAlt,
        borderTop: `1px solid ${C.borderSoft}`,
      }}>
        {loading && <p style={{ margin: 0, color: C.textSoft, fontSize: '13px' }}>Loading…</p>}

        {!loading && invoice && (
          <>
            <div style={{
              border: `1px solid ${C.border}`, borderRadius: '8px',
              overflow: 'hidden', background: '#ffffff',
            }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '560px' }}>
                  <thead>
                    <tr>
                      <th style={{ ...head, textAlign: 'left' }}>Size</th>
                      <th style={{ ...head, textAlign: 'right' }}>Qty</th>
                      <th style={{ ...head, textAlign: 'right' }}>Rate</th>
                      <th style={{ ...head, textAlign: 'right' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.items.map(item => (
                      <tr key={item.id}>
                        <td style={cell}>
                          <div style={{ fontWeight: '600' }}>
                            {mm(item.width_mm)} × {yd(item.length_yards)}
                          </div>
                          <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '2px' }}>
                            {item.tape_type_name} {num(item.micron_value)}µ
                          </div>
                        </td>
                        <td style={{ ...cell, textAlign: 'right', fontWeight: '600' }}>{item.quantity}</td>
                        <td style={{ ...cell, textAlign: 'right' }}>{money(item.rate)}</td>
                        <td style={{ ...cell, textAlign: 'right', fontWeight: '600' }}>{money(item.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Payments against this invoice. Shown in full rather
                than as a total — when a customer disputes what they
                paid, the dates and receipt numbers are the answer. */}
            <div style={{ marginTop: '14px' }}>
              <div style={{
                display: 'flex', alignItems: 'baseline', gap: '10px',
                marginBottom: '8px', flexWrap: 'wrap',
              }}>
                <strong style={{ fontSize: '13px', color: C.text }}>Payments</strong>
                <PaymentBadge status={invoice.payment_status} />
                <span style={{ marginLeft: 'auto', fontSize: '13px', color: C.textSoft }}>
                  Received <strong style={{ color: C.text }}>{money(invoice.paid)}</strong>
                  {' '}of {money(invoice.grand_total)}
                  {num(invoice.balance) >= 1 && (
                    <> · <strong style={{ color: C.danger }}>{money(invoice.balance)} due</strong></>
                  )}
                </span>
              </div>

                            {num(invoice.paid_on_account) >= 1 && (
                <div style={{ fontSize: '12px', color: C.info, marginBottom: '8px' }}>
                  {money(invoice.paid_on_account)} settled from on-account payments — see the customer ledger.
                </div>
              )}
              {(invoice.payments || []).length === 0 ? (
                <div style={{
                  padding: '12px 14px', background: '#ffffff',
                  border: `1px solid ${C.border}`, borderRadius: '8px',
                  fontSize: '13px', color: C.textMuted,
                }}>
                  No payments recorded against this invoice.
                </div>
              ) : (
                <div style={{ border: `1px solid ${C.border}`, borderRadius: '8px', overflow: 'hidden', background: '#ffffff' }}>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '520px' }}>
                      <thead>
                        <tr>
                          <th style={{ ...head, textAlign: 'left' }}>Date</th>
                          <th style={{ ...head, textAlign: 'left' }}>Receipt</th>
                          <th style={{ ...head, textAlign: 'left' }}>Method</th>
                          <th style={{ ...head, textAlign: 'right' }}>Amount</th>
                          <th style={{ ...head, textAlign: 'right' }}>WHT</th>
                          {mayDelete && <th style={{ ...head, width: '52px' }}></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {invoice.payments.map(p => (
                          <tr key={p.id}>
                            <td style={{ ...cell, whiteSpace: 'nowrap', color: C.textSoft }}>
                              {fmtDate(p.payment_date)}
                            </td>
                            <td style={cell}>{p.ref_no || '—'}</td>
                            <td style={{ ...cell, textTransform: 'capitalize' }}>{p.method || '—'}</td>
                            <td style={{ ...cell, textAlign: 'right', fontWeight: '600' }}>{money(p.amount)}</td>
                            <td style={{ ...cell, textAlign: 'right', color: C.textSoft }}>
                              {num(p.wht_amount) > 0 ? money(p.wht_amount) : '—'}
                            </td>
                            {mayDelete && (
                              <td style={{ ...cell, textAlign: 'right' }}>
                                {withinDeleteWindow(p.created_at) ? (
                                  <IconBtn danger title="Delete payment" onClick={() => removePayment(p)}>
                                    <Trash2 size={14} />
                                  </IconBtn>
                                ) : (
                                  <span
                                    title={`Entered more than ${PAYMENT_DELETE_DAYS} days ago`}
                                    style={{ color: C.textMuted, display: 'inline-flex' }}
                                  >
                                    <Lock size={13} />
                                  </span>
                                )}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {invoice.notes && (
              <div style={{ marginTop: '12px', fontSize: '13px', color: C.textSoft }}>
                <strong style={{ color: C.text }}>Notes:</strong> {invoice.notes}
              </div>
            )}
          </>
        )}

        {!loading && !invoice && (
          <p style={{ margin: 0, color: C.danger, fontSize: '13px' }}>Could not load this invoice.</p>
        )}
      </td>
    </tr>
  );
};


// ============================================================
// E. PAYMENT + LEDGER MODALS
// ============================================================

const PaymentModal = ({ customer, onClose, onSaved, notify }) => {
  const [form, setForm] = useState({
    payment_date: today(), invoice_id: '',
    ref_no: '', amount: '', wht_amount: '', method: 'cash', notes: '',
  });
  const [saving, setSaving] = useState(false);

  // Settled invoices are hidden by default — after a year most
  // are closed and scrolling past them is how the wrong one gets
  // picked. The toggle is there for corrections: a payment put
  // against the wrong invoice, or a late withholding-tax entry.
  const [invoices, setInvoices] = useState([]);
  // The customer's opening balance, if they have one. Picked in the
  // same dropdown as the invoices; null when there is none.
  const [opening, setOpening] = useState(null);
  const [showSettled, setShowSettled] = useState(false);
  const [loadingInvoices, setLoadingInvoices] = useState(true);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    let cancelled = false;
    setLoadingInvoices(true);
    axios.get(
      `${API}/tape/sales/customers/${customer.id}/open-invoices`
        + `${showSettled ? '?include_settled=1' : ''}`,
      { headers }
    )
      .then(res => {
        if (cancelled) return;
        setInvoices(res.data.data || []);
        setOpening(res.data.opening || null);
      })
      .catch(() => { if (!cancelled) { setInvoices([]); setOpening(null); } })
      .finally(() => { if (!cancelled) setLoadingInvoices(false); });
    return () => { cancelled = true; };
  }, [customer.id, showSettled]); // eslint-disable-line

  // Withheld tax still settles the invoice, so it counts
  // towards what this payment clears.
  const totalSettled = num(form.amount) + num(form.wht_amount);

  // Offered like an invoice: while anything is due on it, or always
  // when settled ones are shown (for corrections).
  const showOpening = !!opening
    && (num(opening.opening_due) >= 1 || showSettled);

  const save = async () => {
    if (!(num(form.amount) > 0)) return notify('error', 'Amount must be more than 0');
    if (!form.payment_date)      return notify('error', 'Payment date is required');

    setSaving(true);
    try {
      // against_no is derived from the chosen invoice rather than
      // typed, so the printed ledger narration and the stored link
      // can never say different things.
      //
      // 'opening' in the dropdown means the opening balance: no
      // invoice, and the against_opening flag instead.
      const againstOpening = form.invoice_id === 'opening';
      const chosen = againstOpening
        ? null
        : invoices.find(i => String(i.id) === String(form.invoice_id));

      await axios.post(`${API}/tape/sales/payments`, {
        customer_id: customer.id,
        ...form,
        invoice_id: againstOpening ? null : (form.invoice_id || null),
        against_opening: againstOpening,
        against_no: chosen ? chosen.invoice_number : null,
        wht_amount: form.wht_amount || 0,
      }, { headers });
      notify('success', 'Payment received');
      onSaved();
      onClose();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not save payment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <WindowModal
      id={`cust-payment-${customer.id}`}
      title="Receive payment"
      subtitle={`${customer.name} · outstanding ${money(customer.balance)}`}
      onClose={onClose}
      width="560px"
    >
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '16px', marginBottom: '18px',
      }}>
        <Field label="Payment date">
          <TextInput
            type="date"
            value={form.payment_date}
            onChange={e => setForm({ ...form, payment_date: e.target.value })}
          />
        </Field>

        <Field label="Amount">
          <TextInput
            type="number" step="0.01"
            value={form.amount}
            onChange={e => setForm({ ...form, amount: e.target.value })}
            placeholder="0"
          />
        </Field>

        <Field label="Withholding tax (optional)">
          <TextInput
            type="number" step="0.01"
            value={form.wht_amount}
            onChange={e => setForm({ ...form, wht_amount: e.target.value })}
            placeholder="0"
          />
        </Field>

        <Field label="Method">
          <select
            style={inputStyle}
            value={form.method}
            onChange={e => setForm({ ...form, method: e.target.value })}
          >
            <option value="cash">Cash</option>
            <option value="bank">Bank transfer</option>
            <option value="cheque">Cheque</option>
          </select>
        </Field>

        <Field label="Receipt no.">
          <TextInput
            value={form.ref_no}
            onChange={e => setForm({ ...form, ref_no: e.target.value })}
            placeholder="Optional"
          />
        </Field>

        <Field
          label="Against invoice"
          hint="Leave blank for an on-account payment. On-account money clears the opening balance first."
        >
          <select
            style={inputStyle}
            value={form.invoice_id}
            onChange={e => setForm({ ...form, invoice_id: e.target.value })}
            disabled={loadingInvoices}
          >
            <option value="">
              {loadingInvoices
                ? 'Loading invoices…'
                : invoices.length === 0 && !showOpening
                  ? (showSettled ? 'No invoices' : 'Nothing outstanding')
                  : 'On account — no specific invoice'}
            </option>
            {showOpening && (
              <option value="opening">
                Opening balance · {money(opening.opening_balance)}
                {num(opening.opening_due) < 1
                  ? ' · settled'
                  : num(opening.opening_paid) > 0
                    ? ` · ${money(opening.opening_due)} due`
                    : ' · unpaid'}
              </option>
            )}
            {invoices.map(i => (
              <option key={i.id} value={i.id}>
                {i.invoice_number} · {fmtDate(i.invoice_date)} · {money(i.grand_total)}
                {num(i.balance) <= 0
                  ? ' · settled'
                  : num(i.paid) > 0
                    ? ` · ${money(i.balance)} due`
                    : ' · unpaid'}
              </option>
            ))}
          </select>

          <label style={{
            display: 'flex', alignItems: 'center', gap: '7px',
            marginTop: '8px', fontSize: '12px', color: C.textSoft, cursor: 'pointer',
          }}>
            <input
              type="checkbox"
              checked={showSettled}
              onChange={e => {
                setShowSettled(e.target.checked);
                setForm(f => ({ ...f, invoice_id: '' }));
              }}
            />
            Show settled invoices
          </label>
        </Field>
      </div>

      <Field label="Notes">
        <TextInput
          value={form.notes}
          onChange={e => setForm({ ...form, notes: e.target.value })}
          placeholder="Optional"
        />
      </Field>

      {totalSettled > 0 && (
        <div style={{
          marginTop: '18px', padding: '13px 16px', borderRadius: '8px',
          background: C.borderSoft, fontSize: '14px', color: C.textSoft,
        }}>
          Settles <strong style={{ color: C.text }}>{money(totalSettled)}</strong> against the balance
          {num(form.wht_amount) > 0 && ' (including withholding tax)'}
        </div>
      )}

      <div style={{ display: 'flex', gap: '10px', marginTop: '22px' }}>
        <Btn onClick={save} disabled={saving}>
          <Check size={16} /> {saving ? 'Saving…' : 'Save payment'}
        </Btn>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
      </div>
    </WindowModal>
  );
};


const LedgerModal = ({ customer, onClose, notify, onChanged }) => {
  const [fromDate, setFromDate] = useState(DEFAULT_FROM_DATE);
  const [toDate, setToDate]     = useState(today());
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append('from_date', fromDate);
      if (toDate)   params.append('to_date', toDate);

      const res = await axios.get(
        `${API}/tape/sales/customers/${customer.id}/ledger?${params.toString()}`,
        { headers }
      );
      setData(res.data.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, customer.id]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const mayDelete = canDeletePayments();

  // The opening balance arrives as a ledger row when its date is
  // inside the window. For the cards it belongs with Opening, not
  // with Invoiced. Opening + Invoiced − Received still equals
  // Closing.
  const openingInWindow = (data?.transactions || [])
    .filter(t => t.type === 'opening')
    .reduce((s, t) => s + num(t.debit), 0);
  const openingShown  = num(data?.opening_balance) + openingInWindow;
  const invoicedShown = num(data?.total_debit) - openingInWindow;

  // An on-account payment belongs to no invoice, so this screen is
  // the only place it can be seen — and the only place it can be
  // removed.
  const removePayment = async (t) => {
    if (!confirmDeletePayment(t.credit, t.date)) return;
    try {
      await deletePaymentRequest(t.payment_id);
      notify('success', 'Payment deleted');
      load();
      onChanged();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not delete payment');
    }
  };

  const cell = { padding: '9px 12px', fontSize: '13px', borderTop: `1px solid ${C.borderSoft}` };
  const head = {
    ...cell, background: C.rowAlt, fontWeight: '600', color: C.textSoft,
    fontSize: '11px', textTransform: 'uppercase', borderTop: 'none',
  };

  return (
    <WindowModal
      id={`cust-ledger-${customer.id}`}
      title="Customer ledger"
      subtitle={customer.name}
      onClose={onClose}
      width="880px"
    >
      <div style={{ display: 'flex', gap: '14px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div style={{ minWidth: '150px' }}>
          <Field label="From">
            <TextInput type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} />
          </Field>
        </div>
        <div style={{ minWidth: '150px' }}>
          <Field label="To">
            <TextInput type="date" value={toDate} onChange={e => setToDate(e.target.value)} />
          </Field>
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'flex-end', paddingBottom: '1px' }}>
          <PrintButton
            label="Print Ledger"
            filename={`Ledger-${customer.name}-${fromDate}-to-${toDate}`}
            disabled={!data}
            getConfig={() =>
              ledgerConfig({ party: 'customer', data, from_date: fromDate, to_date: toDate })
            }
          />
        </div>
      </div>

      {loading && <p style={{ color: C.textSoft }}>Loading…</p>}

      {!loading && data && (
        <>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
            gap: '12px', marginBottom: '20px',
          }}>
            {[
              ['Opening',  `${money(Math.abs(openingShown))} ${openingShown >= 0 ? 'DR' : 'CR'}`, C.textSoft],
              ['Invoiced', money(invoicedShown),  C.text],
              ['Received', money(data.total_credit), C.text],
              ['Closing',  `${money(Math.abs(data.closing_balance))} ${data.closing_suffix}`,
                           data.closing_balance > 0 ? C.danger : C.success],
            ].filter(([label]) => hasFullAccess('adhesive') || label === 'Invoices').map(([label, value, colour]) => (
              <div key={label} style={{ background: C.rowAlt, borderRadius: '8px', padding: '12px 14px' }}>
                <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>
                  {label}
                </div>
                <div style={{ fontSize: '16px', fontWeight: '700', color: colour, marginTop: '3px' }}>
                  {value}
                </div>
              </div>
            ))}
          </div>

          <div style={{ border: `1px solid ${C.border}`, borderRadius: '8px', overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '640px' }}>
                <thead>
                  <tr>
                    <th style={{ ...head, textAlign: 'left' }}>Date</th>
                    <th style={{ ...head, textAlign: 'left' }}>Narration</th>
                    <th style={{ ...head, textAlign: 'right' }}>Debit</th>
                    <th style={{ ...head, textAlign: 'right' }}>Credit</th>
                    <th style={{ ...head, textAlign: 'right' }}>Balance</th>
                    {mayDelete && <th style={{ ...head, width: '52px' }}></th>}
                  </tr>
                </thead>
                <tbody>
                  {data.transactions.map((t, i) => (
                    <tr key={i}>
                      <td style={{ ...cell, whiteSpace: 'nowrap', color: C.textSoft }}>
                        {fmtDate(t.date)}
                      </td>
                      <td style={cell}>{t.narration}</td>
                      <td style={{ ...cell, textAlign: 'right', color: t.debit ? C.danger : C.textMuted }}>
                        {t.debit ? money(t.debit) : '—'}
                      </td>
                      <td style={{ ...cell, textAlign: 'right', color: t.credit ? C.success : C.textMuted }}>
                        {t.credit ? money(t.credit) : '—'}
                      </td>
                      <td style={{ ...cell, textAlign: 'right', fontWeight: '600' }}>
                        {money(Math.abs(t.balance))} {t.balance >= 0 ? 'DR' : 'CR'}
                      </td>
                      {mayDelete && (
                        <td style={{ ...cell, textAlign: 'right' }}>
                          {t.type === 'payment' && withinDeleteWindow(t.created_at) && (
                            <IconBtn danger title="Delete payment" onClick={() => removePayment(t)}>
                              <Trash2 size={14} />
                            </IconBtn>
                          )}
                          {t.type === 'payment' && !withinDeleteWindow(t.created_at) && (
                            <span
                              title={`Entered more than ${PAYMENT_DELETE_DAYS} days ago`}
                              style={{ color: C.textMuted, display: 'inline-flex' }}
                            >
                              <Lock size={13} />
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                  {data.transactions.length === 0 && (
                    <tr>
                      <td colSpan={mayDelete ? 6 : 5} style={{ ...cell, textAlign: 'center', color: C.textMuted, padding: '32px' }}>
                        No transactions in this period
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <p style={{ fontSize: '12px', color: C.textMuted, margin: '12px 0 0' }}>
            DR means the customer owes us. CR means they have paid more than they were billed.
          </p>
        </>
      )}

      {!loading && !data && <p style={{ color: C.danger }}>Could not load the ledger.</p>}
    </WindowModal>
  );
};


// ============================================================
// F. MAIN COMPONENT
// ============================================================

// The list query carries no line items, so the print button
// fetches the full invoice on click and builds the config.
const fetchInvoiceConfig = async (invoiceId) => {
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  const res = await axios.get(`${API}/tape/sales/${invoiceId}`, { headers });
  return invoicePrintConfig(res.data.data);
};

const SalesTab = () => {
  const [invoices, setInvoices]   = useState([]);
  const [customers, setCustomers] = useState([]);
  const [stock, setStock]         = useState([]);
  const [summary, setSummary]     = useState(null);
  const [loading, setLoading]     = useState(true);
  const [loadingStock, setLoadingStock] = useState(false);
  const [form, setForm]           = useState(null);
  const [saving, setSaving]       = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [payingCustomer, setPayingCustomer] = useState(null);
  const [ledgerCustomer, setLedgerCustomer] = useState(null);
  const [banner, setBanner]       = useState({ type: '', message: '' });
  const [statusFilter, setStatusFilter] = useState('');

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const notify = (type, message) => {
    setBanner({ type, message });
    if (type === 'success') setTimeout(() => setBanner({ type: '', message: '' }), 3000);
  };

  const load = useCallback(async () => {
    try {
      const query = statusFilter ? `?payment_status=${statusFilter}` : '';
      const [i, c, s] = await Promise.all([
        axios.get(`${API}/tape/sales${query}`, { headers }),
        axios.get(`${API}/tape/sales/customers`, { headers }),
        axios.get(`${API}/tape/sales/summary`, { headers }),
      ]);
      setInvoices(i.data.data || []);
      setCustomers(c.data.data || []);
      setSummary(s.data.data || null);
    } catch {
      notify('error', 'Could not load sales. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  // Stock no longer depends on who is buying, so it loads when
  // the form opens rather than after a customer is picked.
  const openForm = async () => {
    setForm(emptyForm());
    setLoadingStock(true);
    try {
      const res = await axios.get(`${API}/tape/sales/stock`, { headers });
      setStock(res.data.data || []);
    } catch {
      setStock([]);
      notify('error', 'Could not load finished stock');
    } finally {
      setLoadingStock(false);
    }
  };

  const closeForm = () => {
    setForm(null);
    setStock([]);
  };

  const save = async () => {
    const f = form;
    if (!f.customer_id)  return notify('error', 'Pick a customer');
    if (!f.invoice_date) return notify('error', 'Invoice date is required');

    const validLines = f.items.filter(
      l => l.stock_id && Number(l.quantity) > 0 && l.rate !== '' && Number(l.rate) >= 0
    );
    if (validLines.length === 0) {
      return notify('error', 'Add at least one complete line');
    }

    setSaving(true);
    try {
      await axios.post(`${API}/tape/sales`, {
        customer_id:  f.customer_id,
        invoice_date: f.invoice_date,
        notes:        f.notes,
        items:        validLines,
      }, { headers });

      notify('success', 'Invoice created');
      closeForm();
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(
      `Delete ${row.invoice_number}? The pieces go back into stock and the balance is reversed.`
    )) return;

    try {
      await axios.delete(`${API}/tape/sales/${row.id}`, { headers });
      notify('success', 'Invoice deleted');
      if (expandedId === row.id) setExpandedId(null);
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not delete');
    }
  };

  // Every active customer, not only those owing. The ledger and
  // payment buttons live in this panel and are the only way to reach
  // a payment at all — filtering on balance > 0 meant that settling a
  // customer's account made their payment history unreachable, and a
  // wrong entry that happened to clear the balance could never be
  // found again.
  const ledgerCustomers = customers.filter(c => c.is_active);

  return (
    <div>
      <Banner
        type={banner.type}
        message={banner.message}
        onClose={() => setBanner({ type: '', message: '' })}
      />

      {/* Summary */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: '14px', marginBottom: '20px',
      }}>
        {[
          ['Sales this month', summary ? money(summary.sales_this_month) : '—', C.text],
          ['Total sales',      summary ? money(summary.total_sales) : '—', C.text],
          ['Outstanding',      summary ? money(summary.outstanding) : '—',
            num(summary?.outstanding) > 0 ? C.danger : C.success],
          ['Invoices',         summary?.invoice_count ?? '—', C.text],
        ].map(([label, value, colour]) => (
          <div key={label} style={{
            background: C.card, border: `1px solid ${C.border}`,
            borderRadius: '10px', padding: '16px 18px',
          }}>
            <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>
              {label}
            </div>
            <div style={{ fontSize: '21px', fontWeight: '700', color: colour, marginTop: '4px' }}>
              {value}
            </div>
          </div>
        ))}
      </div>

      {/* Who owes what — the ledger and payment buttons live here */}
      {hasFullAccess('adhesive') && ledgerCustomers.length > 0 && (
        <div style={{
          background: C.card, border: `1px solid ${C.border}`,
          borderRadius: '12px', overflow: 'hidden', marginBottom: '20px',
        }}>
          <div style={{ padding: '16px 22px', borderBottom: `1px solid ${C.borderSoft}` }}>
            <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: C.text }}>
              Customers
            </h3>
            <p style={{ margin: '3px 0 0', fontSize: '13px', color: C.textSoft }}>
              Balances, payments and ledgers.
            </p>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {ledgerCustomers.map(c => (
                  <tr key={c.id}>
                    <td style={{ ...tdStyle, fontWeight: '600' }}>{c.name}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: C.textSoft, fontSize: '13px' }}>
                      {Number(c.invoice_count) > 0 && `${c.invoice_count} invoice(s)`}
                    </td>
                    <td style={{
                      ...tdStyle, textAlign: 'right', fontWeight: '700',
                      color: num(c.balance) > 0 ? C.danger
                           : num(c.balance) < 0 ? C.info
                           : C.textMuted,
                    }}>
                      {num(c.balance) > 0 ? `${money(c.balance)} DR`
                       : num(c.balance) < 0 ? `${money(Math.abs(c.balance))} CR`
                       : 'Settled'}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', width: '110px' }}>
                      <div style={{ display: 'inline-flex', gap: '7px' }}>
                        <IconBtn title="Ledger" onClick={() => setLedgerCustomer(c)}>
                          <FileText size={15} />
                        </IconBtn>
                        <IconBtn title="Receive payment" onClick={() => setPayingCustomer(c)}>
                          <Wallet size={15} />
                        </IconBtn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Invoices */}
      <div style={{
        background: C.card, border: `1px solid ${C.border}`,
        borderRadius: '12px', overflow: 'hidden',
      }}>
        <div style={{
          padding: '18px 22px', borderBottom: `1px solid ${C.borderSoft}`,
          display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: C.text }}>
              Invoices
            </h3>
            <p style={{ margin: '3px 0 0', fontSize: '13px', color: C.textSoft }}>
              Click an invoice to see its lines.
            </p>
          </div>

          <select
            style={{ ...inputStyle, width: 'auto', minWidth: '150px' }}
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="unpaid">Unpaid</option>
            <option value="partial">Partial</option>
            <option value="paid">Paid</option>
          </select>

          {!form && (
            <Btn onClick={openForm} disabled={customers.length === 0}>
              <Plus size={16} /> New invoice
            </Btn>
          )}
        </div>

        {!form && customers.length === 0 && !loading && (
          <div style={{ padding: '14px 22px', background: C.infoBg, fontSize: '13px', color: C.info }}>
            No customers yet. Add one before invoicing.
          </div>
        )}

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Invoice</th>
                <th style={thStyle}>Customer</th>
                <th style={thStyle}>Date</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Lines</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Pieces</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Total</th>
                <th style={thStyle}>Status</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan="8" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '36px' }}>
                    Loading…
                  </td>
                </tr>
              )}

              {!loading && invoices.map(row => {
                const isOpen = expandedId === row.id;
                return (
                  <React.Fragment key={row.id}>
                    <tr
                      onClick={() => setExpandedId(isOpen ? null : row.id)}
                      style={{ cursor: 'pointer', background: isOpen ? C.rowAlt : 'transparent' }}
                    >
                      <td style={{ ...tdStyle, fontWeight: '700' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px' }}>
                          <Chevron
                            size={15}
                            style={{
                              color: C.textMuted,
                              transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                              transition: 'transform 0.15s',
                            }}
                          />
                          {row.invoice_number}
                        </span>
                      </td>
                      <td style={tdStyle}>{row.customer_name}</td>
                      <td style={{ ...tdStyle, color: C.textSoft }}>{fmtDate(row.invoice_date)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: C.textSoft }}>{row.line_count}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{row.total_pieces}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '700' }}>
                        {money(row.grand_total)}
                        {num(row.balance) >= 1 && num(row.paid) > 0 && (
                          <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '500' }}>
                            {money(row.balance)} due
                          </div>
                        )}
                      </td>
                      <td style={tdStyle}><PaymentBadge status={row.payment_status} /></td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        {/* stopPropagation, or clicking these would
                            also toggle the row open */}
                        <span
                          style={{ display: 'inline-flex', gap: '8px', alignItems: 'center' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <PrintButton
                            variant="small"
                            label="Print"
                            filename={row.invoice_number}
                            getConfig={() => fetchInvoiceConfig(row.id)}
                          />
                                                    {canDeletePayments() && num(row.paid) < 1 && (
                            <IconBtn
                              danger
                              title="Delete invoice"
                              onClick={(e) => { e.stopPropagation(); remove(row); }}
                            >
                              <Trash2 size={15} />
                            </IconBtn>
                          )}
                        </span>
                      </td>
                    </tr>

                    {isOpen && (
                      <InvoiceDetailRow
                        invoiceId={row.id}
                        colSpan={8}
                        notify={notify}
                        onChanged={load}
                      />
                    )}
                  </React.Fragment>
                );
              })}

              {!loading && invoices.length === 0 && (
                <tr>
                  <td colSpan="8" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '40px' }}>
                    No invoices yet. Click "New invoice" to create the first one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {form && (
        <InvoiceForm
          form={form}
          setForm={setForm}
          customers={customers.filter(c => c.is_active)}
          stock={stock}
          loadingStock={loadingStock}
          onSave={save}
          onCancel={closeForm}
          saving={saving}
        />
      )}

      {payingCustomer && (
        <PaymentModal
          customer={payingCustomer}
          onClose={() => setPayingCustomer(null)}
          onSaved={load}
          notify={notify}
        />
      )}

      {ledgerCustomer && (
        <LedgerModal
          customer={ledgerCustomer}
          onClose={() => setLedgerCustomer(null)}
          notify={notify}
          onChanged={load}
        />
      )}
    </div>
  );
};

export default SalesTab;